import { REGISTRATION_NOT_ALLOWED } from "@/defines/tournamentdefines";
import type { Item } from "./collections/itemcollection";
import type Participant from "./participant";
import { VideoMode } from "@/logic/videobridge/videomode";
import { SCORING_METHOD } from "@/defines/scoringmethod";

// used for protocol on partnership
export enum TournamentPartnership {
    ACCEPTED = 0,
    DECLINED = 1,
    NOT_AVAILABLE = 2,
    PAID_BY_PARTNER_OR_CAPTAIN = 3,
};

export enum RegistrationPayment {
    PAYMENT_NONE = 0,
    PAYMENT_REGULAR = 1,
    PAYMENT_INCLUDING_OTHERS = 2,
    PAYMENT_BY_PARTNER_OR_CAPTAIN = 3,
};

export class RegistrationBase {
    teamName = "";
    player: string[] = ["", "", "", ""];
    paymentByCaptain = false;

    /**
     * Returns the parter of the user, indicated with name. The lookup is
     * case insensitive.
     * @param of the user name
     * @returns the parter of the user, indicated with name.
     */
    getPartner(of: string) {
        for (let i = 0; i < 4; i++) {
            if (this.player[i].toLowerCase() === of.toLowerCase()) {
                const pi = (i % 2 === 1) ? -1 : 1;
                return this.player[i + pi];
            }
        }
        return "";
    };
}

export class RegistrationRequest extends RegistrationBase {
    static readonly NONE = 1;
    static readonly REQUEST = 2;
    static readonly REQUEST_AUTOMATCHING = 4;

    kind = RegistrationRequest.NONE;
}

export class RegistrationState extends RegistrationBase {
    static readonly NONE = 1;
    static readonly REQUESTED = 2;
    static readonly MATCHED = 3;
    static readonly AUTOMATCHING = 4;

    state = RegistrationState.NONE;
    accepted = [false, false, false, false];

    getPendingNames() {
        const list: string[] = [];

        for (let i = 0; i < 4; i++) {
            if (this.player[i].length > 0) {
                if (this.accepted[i] === false) {
                    list.push(this.player[i]);
                }
            }
        }

        return list;
    }
}

export enum STATUS {
    STARTING = 0,
    READY_TO_START = 1,
    INVITING_USERS = 2,
    RUNNING = 3,
    FINISHED = 4,
    DESTROY = 5,
}

export enum PARTICIPATE {
    NORMAL = 1,
    SUBSTITUTE = 2,
    REPLACE = 3,
    TAKE_SEAT = 4,
}

// next round config
export enum NEXT_ROUND {
    ON_BUTTON_ONLY = 0,  // oude adminready = false
    NOW = 1,    // oude adminready = true
    ON_TIME = 2,
    ON_PLAYED = 3,
    ON_TIME_AND_PLAYED = 4,
}
// TournamentSchedulingMethod
export enum SCHEDULING {
    UNKNOWN = 0,
    RANDOM = 1,
    SWISS = 2,
    CLUB = 3,
};

export enum TournamentStructure {
    MatchpointsPairs,
    MatchpointsPairsIndividual,
    MatchpointsButler,
    MatchpointsButlerIndividual,
    Teams,
    TeamsHalftimeSwitch,
};

/*
    public static final int OBSERVER_FLAG_NONE = 0;
    public static final int OBSERVER_FLAG_PARAMETERS = 1;
    public static final int OBSERVER_FLAG_STATUS = 2;
    public static final int OBSERVER_FLAG_PARTICIPANTS = 4;
    public static final int OBSERVER_FLAG_REGISTRATION = 8;
    public static final int OBSERVER_FLAG_INFO = 16;
    public static final int OBSERVER_FLAG_ALL = 0xFFFF;
    */

/**
 * TournamentBase is used for describing a tournament in a message, 
 * like a Partnership request.
 */
export class TournamentBase implements Item {
    id = 0; // must be zero for current tournament
    name = "";
    startTime = new Date();

    fee = 0;
    feeGuests = 0;

    playersPerParticipant = 0;

    constructor(id?: number) {
        this.id = id ?? 0;
    }

    isTeams() {
        return this.playersPerParticipant == 4;
    }

    hasFee() {
        return this.fee > 0 || this.feeGuests > 0;
    }
}

export default class Tournament extends TournamentBase {
    director = "";
    dbId = 0;
    clubId = 0;

    scoringMethod = 0;
    currentRound = 0;
    roundsTotal = 0;
    twoHalves = false;
    isSecondHalf = false;
    boardsPerRound = 0;
    secondsPerRound = 0;

    secondsBeforeStart = 0;
    registrationAllowed = false;
    reasonRegNotAllowed: number = REGISTRATION_NOT_ALLOWED.NONE;
    levelMin = 0;
    levelMax = 0;

    notice = "";
    sectionCount = 0;
    participantsTotal = 0;
    nextRoundConfig = 0;
    schedulingMethod = SCHEDULING.UNKNOWN;
    autoMatchEnabled = true;

    complete = false; // do we have all the data of this class

    dontAskForSubstitution = false;
    pbnFilename = "";
    waitingForAnswer = false;
    status = 0;

    videoMode = VideoMode.NONE;

    allowSpectators = true;
    canClaim = true; // users can claim. In Sabi tournaments users cannot.
    canCallDirector = true; // users can call a TD. In Sabi tournaments users cannot.
    isSabi = false; // if it is a sabi tournament

    registration = new RegistrationState();
    participants = new Map<number, Participant>();

    reset() {
        // TODO... remove the reset and make it that a new reactive object is created from Tournament
        this.director = "";
        this.id = 0; // must be zero for current tournament
        this.dbId = 0;
        this.clubId = 0;
        this.name = "";
        this.playersPerParticipant = 0;
        this.scoringMethod = 0;
        this.currentRound = 0;
        this.roundsTotal = 0;
        this.boardsPerRound = 0;
        this.secondsPerRound = 0;
        this.startTime = new Date();
        this.secondsBeforeStart = 0;
        this.levelMin = 0;
        this.levelMax = 0;
        this.dontAskForSubstitution = false;
        this.notice = "";
        this.participantsTotal = 0;
        this.fee = 0;
        this.feeGuests = 0;
        this.registrationAllowed = false;
        this.reasonRegNotAllowed = REGISTRATION_NOT_ALLOWED.NONE;
        this.registration = new RegistrationState();
        this.nextRoundConfig = 0;
        this.schedulingMethod = SCHEDULING.UNKNOWN;
        this.autoMatchEnabled = true;
        this.pbnFilename = "";
        this.waitingForAnswer = false;
        this.status = 0;
        this.sectionCount = 0;
        this.participants.clear();
        this.complete = false;
        this.videoMode = VideoMode.NONE;
        this.canClaim = true;
        this.canCallDirector = true;
        this.allowSpectators = true;
        this.isSabi = false;
        this.twoHalves = false;
        this.isSecondHalf = false;
    }

    isPlayerMovementAllowed() {
        switch (this.status) {
            case STATUS.INVITING_USERS:
            case STATUS.READY_TO_START:
            case STATUS.RUNNING:
                return false;
            case STATUS.FINISHED:
            default:
                return true;
        }
    }

    isPreRunning() {
        return this.status < STATUS.RUNNING;
    }


    isRunning() {
        return this.status === STATUS.RUNNING;
    }

    isFinished() {
        return this.status >= STATUS.FINISHED;
    }

    isRegistered() {
        return this.registration.state !== RegistrationState.NONE;
    }


    hashCode() {
        const s = this.startTime.toString() + this.name;

        let hash = 0;
        for (const c of s) {
            const code = c.charCodeAt(0);
            hash = ((hash << 5) - hash) + code;
            hash = hash & hash; // Convert to 32bit integer
        }
        return hash;
    }


    equals(other: Tournament) {
        if (other === undefined) {
            return false;
        }

        if (other.id !== 0 && this.id !== 0) {
            return other.id === this.id;
        } else {
            return other.dbId === this.dbId;
        }
    }

    setRegistration(ps: RegistrationState) {
        this.registration = ps;
    }

    isParticipant(member: string) {
        return this.getParticipant(member) !== undefined;
    }

    getParticipant(member: string) {
        this.participants.values();

        for (const p of this.participants.values()) {
            for (const player of p.players) {
                if (player && player.name === member) {
                    return p;
                }
            }
        }
        return undefined;
    }

    hasVideo() {
        return this.videoMode !== VideoMode.NONE;
    }

    getStructure(): TournamentStructure {
        switch (this.playersPerParticipant) {
            case 4:
                return this.twoHalves
                    ? TournamentStructure.TeamsHalftimeSwitch
                    : TournamentStructure.Teams;

            case 2:
                return this.scoringMethod === SCORING_METHOD.BUTLER
                    ? TournamentStructure.MatchpointsButler
                    : TournamentStructure.MatchpointsPairs;
            case 1:
            default:
                return this.scoringMethod === SCORING_METHOD.BUTLER
                    ? TournamentStructure.MatchpointsButlerIndividual
                    : TournamentStructure.MatchpointsPairsIndividual;
        }
    }
}